<template>
  <div
    class="chat-input-container"
    :class="{ 'is-dragover': isDragActive }"
    @paste="$emit('paste', $event)"
    @dragenter.capture="dragHandlers.onDragEnter"
    @dragover.capture="dragHandlers.onDragOver"
    @dragleave.capture="dragHandlers.onDragLeave"
    @drop.capture="dragHandlers.onDrop"
  >
    <div v-if="isDragActive" class="drop-overlay">
      <div class="drop-overlay-content">
        <FileIcon />
        <span>Drop files to attach</span>
      </div>
    </div>

    <div v-if="displayErrorMessage" class="chat-error">
      {{ displayErrorMessage }}
    </div>
    <Composer
      ref="composer"
      @submit="$emit('submit', $event)"
      @change="onComposerChange"
      :workspace-id="workspaceId"
      :reference-search-organization-id="referenceSearchOrganizationId"
      :reference-context-workspace-id="referenceContextWorkspaceId"
      :integration-organization-ids="integrationOrganizationIds"
      :mentions-enabled="mentionsEnabled"
      @integration-candidates-change="onIntegrationCandidatesChange"
    >
      <template #placeholder>
        <template v-if="voiceStatus === 'recording'">Listening…</template>
        <slot v-else name="placeholder" />
      </template>
    </Composer>
    <div class="chat-input-bottom-row">
      <ButtonMenu placement="top-start">
        <template #btn>
          <BaseButton kind="auxiliary" type="tertiary">
            <template #icon>
              <PlusIcon />
            </template>
          </BaseButton>
        </template>
        <template #content="{ props: hideMenu }">
          <BaseButton
            kind="auxiliary"
            text="Add attachment"
            is-expanded
            @click="triggerFileInput"
          >
            <template #icon>
              <FileIcon />
            </template>
          </BaseButton>
          <BaseButton
            kind="auxiliary"
            text="Reference an object"
            is-expanded
            @click="linkObject"
          >
            <template #icon>
              <ReferenceIcon target="object" />
            </template>
          </BaseButton>
          <ButtonMenu
            placement="right-start"
            :offset="[0, -4]"
            class="integration-submenu"
          >
            <template #btn>
              <BaseButton kind="auxiliary" text="Use integration" is-expanded>
                <template #icon>
                  <IntegrationIcon />
                </template>
                <template #right>
                  <ChevronRightIcon />
                </template>
              </BaseButton>
            </template>
            <template #content>
              <div
                v-if="integrationMenuState.loading"
                class="integration-submenu-state"
              >
                <Spinner />
                <span>Loading integrations…</span>
              </div>
              <template v-else-if="integrationMenuState.candidates.length > 0">
                <BaseButton
                  v-for="candidate in integrationMenuState.candidates"
                  :key="`${candidate.integrationKey}:${candidate.connectionId}`"
                  kind="auxiliary"
                  :text="candidate.name"
                  is-expanded
                  @click="selectIntegrationCandidate(candidate, hideMenu)"
                >
                  <template #icon>
                    <img
                      v-if="candidate.logoUri"
                      :src="candidate.logoUri"
                      class="integration-submenu-logo"
                    />
                    <IntegrationIcon v-else />
                  </template>
                  <template #right>
                    <span class="integration-submenu-details">
                      {{ integrationCandidateDetails(candidate) }}
                    </span>
                  </template>
                </BaseButton>
              </template>
              <div v-else class="integration-submenu-state">
                No integration configured
              </div>
            </template>
          </ButtonMenu>
        </template>
      </ButtonMenu>
      <input
        ref="fileInput"
        type="file"
        multiple
        class="hidden-file-input"
        @change="$emit('file-input-change', $event)"
      />
      <ChatAttachments
        :attachments="attachments"
        :uploading="uploadingFiles"
        @remove="$emit('remove-attachment', $event)"
      />
      <VoiceWaveform
        v-if="voiceStatus === 'recording'"
        :stream="voiceInput.mediaStream.value"
        class="voice-waveform"
      />
      <BaseButton
        v-if="voiceStatus !== 'recording' && composerIsEmpty"
        kind="auxiliary"
        type="tertiary"
        :tooltip="isStarting ? undefined : 'Voice input'"
        :state="isStarting ? 'disabled' : voiceButtonState"
        class="voice-button voice-button-idle"
        @click="onVoiceStart"
      >
        <template #icon>
          <Spinner v-if="isStarting" />
          <MicrophoneIcon v-else />
        </template>
      </BaseButton>
      <BaseButton
        v-if="voiceStatus === 'recording'"
        kind="auxiliary"
        type="tertiary"
        tint="red"
        tooltip="Cancel recording"
        class="voice-button"
        @click="onVoiceCancel"
      >
        <template #icon>
          <CloseIcon />
        </template>
      </BaseButton>
      <BaseButton
        v-if="voiceStatus !== 'recording' && !composerIsEmpty"
        kind="main"
        type="primary"
        :state="resolvedSubmitButtonState"
        class="submit-button"
        @click="submitComposer"
      >
        <template #icon>
          <ArrowUpSubmitIcon />
        </template>
      </BaseButton>
      <BaseButton
        v-if="voiceStatus === 'recording'"
        kind="main"
        type="primary"
        tooltip="Confirm transcript"
        class="submit-button"
        @click="onVoiceConfirm"
      >
        <template #icon>
          <CheckmarkIcon />
        </template>
      </BaseButton>
    </div>
  </div>
</template>

<script lang="ts">
import type { Node as ProseMirrorNode } from "prosemirror-model";
import {
  computed,
  defineComponent,
  nextTick,
  onBeforeUnmount,
  ref,
  watch,
  type PropType,
} from "vue";

import type { OrgId, WorkspaceId } from "@ctrl/controller-rpc";

import BaseButton, { type ButtonState } from "@/components/base/BaseButton.vue";
import Composer, {
  type ComposerIntegrationMenuState,
} from "@/components/editor/Composer.vue";
import { utf8ToHex } from "@/components/editor/markup-to-editor-nodes";
import ButtonMenu from "@/components/elements/ButtonMenu.vue";
import ArrowUpSubmitIcon from "@/components/icons/ArrowUpSubmitIcon.vue";
import CheckmarkIcon from "@/components/icons/CheckmarkIcon.vue";
import ChevronRightIcon from "@/components/icons/ChevronRightIcon.vue";
import CloseIcon from "@/components/icons/CloseIcon.vue";
import IntegrationIcon from "@/components/icons/IntegrationIcon.vue";
import MicrophoneIcon from "@/components/icons/MicrophoneIcon.vue";
import PlusIcon from "@/components/icons/PlusIcon.vue";
import ReferenceIcon from "@/components/icons/ReferenceIcon.vue";
import * as inject from "@/components/inject";
import Spinner from "@/components/Spinner.vue";
import { useVoiceInput } from "@/composables/useVoiceInput";
import { voiceSounds } from "@/utils/voiceSounds";

import ChatAttachments from "./ChatAttachments.vue";
import FileIcon from "./FileIcon.vue";
import type { ChatAttachment, UploadingFile } from "./useAttachments";
import { useFileDrop } from "./useFileDrop";
import VoiceWaveform from "./VoiceWaveform.vue";

export interface ChatInputHandle {
  submit: () => void;
  focusEditor: () => void;
  setValue: (value: string) => void;
  linkObject: () => void;
  insertIntegration: (integrationKey: string, connectionId?: string) => void;
}

export default defineComponent({
  name: "ChatInput",
  components: {
    ArrowUpSubmitIcon,
    BaseButton,
    ButtonMenu,
    ChatAttachments,
    CheckmarkIcon,
    ChevronRightIcon,
    CloseIcon,
    Composer,
    FileIcon,
    IntegrationIcon,
    MicrophoneIcon,
    PlusIcon,
    ReferenceIcon,
    Spinner,
    VoiceWaveform,
  },
  props: {
    workspaceId: {
      type: String as PropType<WorkspaceId>,
      required: true,
    },
    mentionsEnabled: {
      type: Boolean,
      default: false,
    },
    referenceSearchOrganizationId: {
      type: String as PropType<OrgId | null>,
      default: null,
    },
    referenceContextWorkspaceId: {
      type: String as PropType<WorkspaceId | null>,
      default: null,
    },
    integrationOrganizationIds: {
      type: Array as PropType<OrgId[] | undefined>,
      default: undefined,
    },
    attachments: {
      type: Array as PropType<ChatAttachment[]>,
      required: true,
    },
    uploadingFiles: {
      type: Array as PropType<UploadingFile[]>,
      default: () => [],
    },
    submitButtonState: {
      type: String as PropType<ButtonState>,
      default: "default",
    },
    errorMessage: {
      type: String as PropType<string | null>,
      default: null,
    },
  },
  emits: {
    submit: (_doc: ProseMirrorNode) => true,
    paste: (_event: ClipboardEvent) => true,
    "file-input-change": (_event: Event) => true,
    "remove-attachment": (_index: number) => true,
    "drop-files": (_files: File[]) => true,
  },
  setup(props, { emit }) {
    const controller = inject.controller();
    const profile = inject.profile();
    const voiceInput = useVoiceInput(controller, profile);
    onBeforeUnmount(() => voiceInput.dispose());

    const composer = ref<InstanceType<typeof Composer> | null>(null);
    const fileInput = ref<HTMLInputElement | null>(null);
    const integrationMenuState = ref<ComposerIntegrationMenuState>({
      candidates: [],
      loading: true,
    });

    const insertIntegration = async (
      integrationKey: string,
      connectionId?: string
    ) => {
      const current = composer.value?.getValue?.() ?? "";
      const prefix = current.trimEnd();
      const connectionToken = connectionId ? `:${utf8ToHex(connectionId)}` : "";
      const token = `>integration:${utf8ToHex(integrationKey)}${connectionToken}`;
      const newValue = prefix.length > 0 ? `${prefix} ${token}` : token;
      composer.value?.setValue?.(newValue);
      // ProseMirrorEditor's doc watcher applies the new value one tick after
      // the prop change (its callback itself awaits nextTick), so wait two
      // ticks before placing the caret at the end of the inserted content.
      await nextTick();
      await nextTick();
      composer.value?.focusEditorEnd?.();
    };

    const voiceStatus = voiceInput.status;
    const voiceErrorMessage = voiceInput.errorMessage;

    const composerIsEmpty = ref(true);

    const onComposerChange = (value: string) => {
      composerIsEmpty.value = value.trim().length === 0;
    };

    const voiceButtonState = computed<ButtonState>(() => {
      if (props.submitButtonState === "loading") return "disabled";
      return "default";
    });

    const resolvedSubmitButtonState = computed<ButtonState>(() => {
      if (props.submitButtonState === "loading") return "loading";
      if (props.submitButtonState === "disabled") return "disabled";
      if (composerIsEmpty.value && voiceStatus.value !== "recording") {
        return "disabled";
      }
      return "default";
    });

    watch(voiceStatus, (status) => {
      if (status === "idle") {
        composerIsEmpty.value =
          (composer.value?.getValue?.() ?? "").trim().length === 0;
      }
    });

    const displayErrorMessage = computed(() => {
      const voice = voiceErrorMessage.value;
      if (voice) return voice;
      return props.errorMessage ?? null;
    });

    const savedComposerValue = ref("");
    const isStarting = ref(false);

    watch(voiceStatus, (status) => {
      if (status === "recording") {
        isStarting.value = false;
        voiceSounds.ready();
      } else if (status === "idle") {
        isStarting.value = false;
      }
    });

    watch(voiceInput.transcript, (transcript) => {
      if (voiceStatus.value !== "recording") return;
      const prefix = savedComposerValue.value.trimEnd();
      const next = prefix.length > 0 ? `${prefix} ${transcript}` : transcript;
      composer.value?.setValue?.(next);
    });

    async function onVoiceStart(): Promise<void> {
      if (voiceButtonState.value === "disabled") return;
      savedComposerValue.value = composer.value?.getValue?.() ?? "";
      isStarting.value = true;
      await voiceInput.start();
    }

    function onVoiceCancel(): void {
      voiceSounds.cancel();
      voiceInput.cancel();
      composer.value?.setValue?.(savedComposerValue.value);
    }

    async function onVoiceConfirm(): Promise<void> {
      voiceSounds.done();
      await voiceInput.stop();
      // transcript is already live in the composer via the watcher
    }

    const triggerFileInput = () => {
      fileInput.value?.click();
    };

    const linkObject = () => {
      composer.value?.insertTextAtEnd?.("[[");
    };

    const onIntegrationCandidatesChange = (
      state: ComposerIntegrationMenuState
    ) => {
      integrationMenuState.value = state;
    };

    const integrationCandidateDetails = (
      candidate: ComposerIntegrationMenuState["candidates"][number]
    ) =>
      candidate.organizationName
        ? `${candidate.integrationKey} - ${candidate.organizationName}`
        : candidate.integrationKey;

    const selectIntegrationCandidate = (
      candidate: ComposerIntegrationMenuState["candidates"][number],
      hideMenu: () => void
    ) => {
      insertIntegration(candidate.integrationKey, candidate.connectionId);
      hideMenu();
    };

    const { isDragActive, dragHandlers } = useFileDrop((files) => {
      emit("drop-files", files);
    });

    const submit = () => {
      composer.value?.submit?.();
    };

    const focusEditor = () => {
      composer.value?.focusEditor?.();
    };

    const setValue = (value: string) => {
      composer.value?.setValue?.(value);
    };

    const submitComposer = () => {
      submit();
    };

    return {
      composer,
      fileInput,
      voiceInput,
      voiceStatus,
      isStarting,
      displayErrorMessage,
      voiceButtonState,
      resolvedSubmitButtonState,
      composerIsEmpty,
      onComposerChange,
      onVoiceStart,
      onVoiceCancel,
      onVoiceConfirm,
      triggerFileInput,
      linkObject,
      integrationMenuState,
      onIntegrationCandidatesChange,
      integrationCandidateDetails,
      selectIntegrationCandidate,
      submit,
      focusEditor,
      setValue,
      submitComposer,
      isDragActive,
      dragHandlers,
      insertIntegration,
    };
  },
});
</script>

<style scoped lang="scss">
@use "@/styles/agent" as *;

:deep(.prosemirror-after-content) {
  display: none;
}

:deep(.composer) {
  max-height: 220px;
  overflow-x: hidden;
  overflow-y: auto;
}

.chat-input-container {
  position: relative;
}

.drop-overlay {
  @include agent-drop-overlay;
}

.drop-overlay-content {
  @include agent-drop-overlay-content;
}

.integration-submenu {
  width: 100%;
}

.integration-submenu-logo {
  width: 14px;
  height: 14px;
  object-fit: contain;
  border-radius: $border-radius-s;
}

.integration-submenu-details {
  @extend %font-small-regular;
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: $mono;
  @include themed() {
    color: t($grey-60);
  }
}

.integration-submenu-state {
  @extend %font-caption-regular;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  @include themed() {
    color: t($grey-60);
  }
}

.chat-input-bottom-row {
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  gap: 8px;
  margin-top: 24px;

  .voice-button,
  .submit-button {
    padding: 6px !important;
  }

  .voice-waveform {
    flex: 1;
    min-width: 0;
    align-self: center;
  }

  .voice-button {
    flex-shrink: 0;
    // only push right when the waveform isn't present (idle state)
    &.voice-button-idle {
      margin-inline-start: auto;
    }
  }

  .submit-button {
    flex-shrink: 0;
    margin-inline-start: auto;
    margin-bottom: 0;
  }

  :deep(.icon),
  :deep(svg) {
    height: 16px !important;
    width: 16px !important;
  }

  :deep(.chat-attachments) {
    padding-bottom: 0;
  }
}
</style>
